/**
 * RFP Agent (Keystone / GovEarner) dedicated landing page.
 * Shown at root when unauthenticated. Enterprise futuristic Digital Pulse aesthetic.
 */

import React from 'react';
import { Globe, FileCheck, Building2, Award, FileText, Database, Users, Search } from 'lucide-react';
import { TraceKitConfig } from '../../types/config';
import { LandingHeroShell } from './LandingHeroShell';
import { getLandingBranding } from './landingBranding';

export interface LandingPageProps {
  config: TraceKitConfig;
  /** Login URL; use /login for root (no app subpath) so it works when deployed without /keystone-rfp */
  loginHref: string;
}

const OPPORTUNITY_SOURCES = [
  { label: 'Merx', icon: FileCheck },
  { label: 'World Bank', icon: Building2 },
  { label: 'Bids & Tender', icon: Award },
  { label: 'SAM.gov', icon: Globe },
  { label: 'Canada Buys', icon: FileCheck },
  { label: 'Canada Grants', icon: Building2 },
  { label: 'Federal Register', icon: FileText },
  { label: 'Victoria', icon: Globe },
  { label: 'Vancouver', icon: Globe },
  { label: 'Metro Vancouver', icon: Building2 },
  { label: 'Alberta', icon: Globe },
];

const STATS = [
  { strong: '30-50%', span: 'Time reduction per proposal' },
  { strong: '25%', span: 'More opportunities evaluated' },
  { strong: '15,000+', span: 'Documents searchable' },
  { strong: '90,000+', span: 'Entities indexed' },
  { strong: 'EGBC-ready', span: 'Compliance built-in' },
];

const FEATURES = [
  {
    title: 'Proposal Management',
    icon: FileText,
    description: 'List, search, and manage proposals. RFP analysis, requirement extraction, pipeline, and review workflow. Export to Word and PDF with citations and evidence.',
  },
  {
    title: 'Government Data & Opportunities',
    icon: Database,
    description: 'Integrations with Bids & Tender, Bonfire, Merx, and more. Shortlist opportunities, filter by 9 service lines and 55 cities across 6 provinces. Deduplication across sources.',
  },
  {
    title: 'Staff Management',
    icon: Users,
    description: 'Match staff to RFP requirements by qualifications, experience, and expertise. Assign team members to proposal sections and track availability.',
  },
  {
    title: 'Knowledge Base & AI Search',
    icon: Search,
    description: 'Universal AI search across 15,000+ documents. Evidence retrieval and citations for every claim. EGBC-compliant traceability.',
  },
];

const STEPS = [
  'Find opportunity',
  'Download RFP & addendums',
  'Upload to Documents',
  'Notify team',
  'Generate template & proposal',
  'Review, approve, export',
];

export function LandingPage({ config, loginHref }: LandingPageProps) {
  const branding = getLandingBranding(config);
  const primaryColor = branding.primaryColor;
  const secondaryColor = branding.secondaryColor;

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        color: '#1f2937',
        lineHeight: 1.6,
      }}
    >
      <LandingHeroShell
        branding={branding}
        loginHref={loginHref}
        heading="AI-Powered Proposal Intelligence"
        gradientKeyword="Intelligence"
        description="Discover opportunities, generate evidence-based proposals, and streamline your bidding process—from RFP to submission."
        marqueeItems={OPPORTUNITY_SOURCES.map((s) => ({ label: s.label, icon: s.icon }))}
        showAbstractDashboard={false}
        compactVerticalSpacing
        heroVariant={config.theme?.allowToggle === false && config.theme?.defaultMode === 'light' ? 'light' : 'dark'}
      />

      {/* Smooth transition from hero to light content */}
      <div
        className="h-12 w-full sm:h-14"
        style={{
          background: config.theme?.allowToggle === false && config.theme?.defaultMode === 'light'
            ? 'linear-gradient(180deg, #f1f5f9 0%, #f8fafc 50%, #f9fafb 100%)'
            : 'linear-gradient(180deg, #1e293b 0%, #475569 40%, #f9fafb 100%)',
        }}
      />

      <main className="bg-[#f9fafb]">
        <div className="mx-auto w-full max-w-[min(92rem,calc(100%-1.25rem))] px-3 py-10 sm:px-4 md:py-14 lg:px-6">
          {/* Why + Platform Features — wide, borderless, side by side on large screens */}
          <div className="mb-16 grid gap-12 lg:grid-cols-2 lg:gap-14 xl:gap-20 lg:items-start">
            <section>
              <h2 className="text-center lg:text-left text-3xl font-bold tracking-tight text-gray-900 sm:text-[1.75rem]">
                Why {branding.appName}?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-center text-[17px] leading-relaxed text-gray-600 lg:mx-0 lg:text-left">
                Before: 60 hours per proposal. With {branding.appName}: 30-50% time reduction, automatic requirement extraction, AI-powered drafts in 2-5 minutes, and every claim backed by your knowledge base.
              </p>
              <div className="mt-10 grid grid-cols-2 place-items-center sm:place-items-start gap-x-8 gap-y-8 sm:grid-cols-3">
                {STATS.map((item, i) => (
                  <div key={i} className="min-w-0 text-left">
                    <p className="text-2xl font-bold text-center tabular-nums tracking-tight sm:text-left sm:text-[1.65rem]" style={{ color: primaryColor }}>
                      {item.strong}
                    </p>
                    <p className="mt-1.5 text-[13px] leading-snug text-gray-600 sm:text-sm">{item.span}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-center lg:text-left text-3xl font-bold tracking-tight text-gray-900 sm:text-[1.75rem]">
                Platform Features
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-center text-[17px] leading-relaxed text-gray-600 lg:mx-0 lg:text-left">
                Everything you need to win government and enterprise proposals.
              </p>
              <div className="mt-10 grid gap-9 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 lg:grid-cols-1 xl:grid-cols-2">
                {FEATURES.map((f, i) => (
                  <div key={i} className="flex gap-4">
                    <f.icon
                      className="mt-0.5 h-6 w-6 shrink-0"
                      strokeWidth={1.75}
                      style={{ color: primaryColor }}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <h3 className="text-[15px] font-semibold leading-snug text-gray-900">{f.title}</h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-gray-600 sm:text-sm">{f.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-12" />

          {/* How It Works */}
          <section className="mb-16">
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-4 tracking-tight">
              How It Works
            </h2>
            <p className="text-center text-gray-600 max-w-xl mx-auto mb-12 text-lg">
              From opportunity discovery to submission in six simple steps.
            </p>
            <ol className="grid grid-cols-2 md:grid-cols-3 gap-4 list-none p-0 m-0">
              {STEPS.map((step, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 rounded-xl border border-gray-200/80 bg-white px-5 py-4 shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-300/80"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-gray-700 font-medium text-sm">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Document progression CTA */}
          <section
            className="rounded-2xl border p-8 mb-16"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}06 100%)`,
              borderColor: `${primaryColor}40`,
            }}
          >
            <p className="m-0 text-gray-800 text-base leading-relaxed">
              <strong className="font-semibold" style={{ color: primaryColor }}>
                Document progression:
              </strong>{' '}
              Track proposals from uploaded → drafted → in review → approved. See status at a glance and move through review and approval in one place.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-4 sm:py-5 text-center">
        <div className="mx-auto w-full max-w-[min(92rem,calc(100%-1.25rem))] px-3 sm:px-4 lg:px-6">
          <p className="text-gray-900 font-semibold text-base sm:text-lg leading-snug">{branding.appName}</p>
          {branding.companyName && (
            <p className="mt-0.5 text-sm text-gray-500 leading-snug">{branding.companyName}</p>
          )}
        </div>
      </footer>
    </div>
  );
}
